import { makeObservable } from 'mobx';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { Common } from 'src/domains/common/Common';
import { apiSendReminderRequest } from 'src/api_openapi/v2/compliance/apiSendReminder';
import { apiGetReminderRequest } from 'src/api_openapi/v2/compliance/apiGetReminder';
import { ComplianceReminderType } from 'src/api_openapi/v2/compliance/schemas';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { runAsync } from 'src_common/common/mobx-utils/runAsync';

export type AccountReviewData = Pick<ComplianceReminderType, 'lastReminderDate' | 'reminderFrequency'>;

export type FetchAccountReviewResult = { success: true; data: AccountReviewData | null } | { success: false };

export class AccountReviewReminderState {
    public static get = AutoWeakMap.create((common: Common) => new AccountReviewReminderState(common));

    public readonly accountReviewResource: Resource<AccountReviewData | null>;

    private constructor(private readonly common: Common) {
        makeObservable(this);

        this.accountReviewResource = new Resource(async () => {
            const result = await this.fetchAccountReview();
            return result.success ? result.data : null;
        });
    }

    public async fetchAccountReview(): Promise<FetchAccountReviewResult> {
        const { gatewayApiHost } = this.common.envVariables;
        const userId = this.common.session.userId;

        if (gatewayApiHost === null || userId === null) {
            return { success: false };
        }

        try {
            const response = await apiGetReminderRequest(
                gatewayApiHost,
                { accountId: userId },
                { authorization: `Bearer ${this.common.session.currentJwt}` }
            );

            if (response.status === 200) {
                if (response.body.length > 0) {
                    const reminderData = response.body.find((item) => item.reminderType === 'ndl');
                    if (reminderData) {
                        return {
                            success: true,
                            data: {
                                lastReminderDate: reminderData.lastReminderDate,
                                reminderFrequency: reminderData.reminderFrequency,
                            },
                        };
                    }
                }
                return { success: true, data: null };
            }

            return { success: false };
        } catch (error) {
            console.error('Error fetching account review:', error);
            return { success: false };
        }
    }

    public async sendAccountReviewReminder(reminderFrequency: number, lastReminderDate: string): Promise<boolean> {
        const { gatewayApiHost, universe } = this.common.envVariables;
        const userId = this.common.session.userId;

        if (gatewayApiHost === null || userId === null) {
            console.error('Unknown gatewayApiHost or userId');
            return false;
        }

        try {
            const response = await apiSendReminderRequest(
                gatewayApiHost,
                {
                    requestBody: {
                        accountId: userId,
                        universe: universe,
                        lastReminderDate: lastReminderDate,
                        reminderFrequency: reminderFrequency,
                        reminderType: 'ndl',
                    },
                },
                { authorization: `Bearer ${this.common.session.currentJwt}` }
            );

            if (response.status === 200 || response.status === 201) {
                return true;
            } else {
                console.error('Failed to send reminder:', response);
                return false;
            }
        } catch (error) {
            console.error('Error sending reminder:', error);
            return false;
        }
    }

    // TODO: Remove when backend handles this automatically (TMA-1326)
    // https://playbookengineering.atlassian.net/browse/TMA-1326
    public ensureDefaultReminderExists(): void {
        runAsync(async () => {
            try {
                const result = await this.fetchAccountReview();

                if (result.success && (result.data === null || result.data.lastReminderDate === null)) {
                    const lastReminderDate = new Date().toISOString();
                    await this.sendAccountReviewReminder(180, lastReminderDate);
                    await this.accountReviewResource.refresh();
                }
            } catch (error) {
                console.error('Failed to ensure default reminder:', error);
            }
        });
    }
}
